import Cookies from 'js-cookie';
import get from 'lodash/get.js';

import axios from '@/plugins/axios.js';
import { i18n } from '@/plugins/i18n.js';
import moment from '@/plugins/vue-moment.js';
import ImpersonationExpiryMonitor from '@/services/ImpersonationExpiryMonitor.js';
import sessionMonitor from '@/services/SessionMonitor.js';
import store from '@/store/Index.js';
import ErrorPackage from '@/utilities/ErrorPackage.js';
import { readBlobAsText } from '@/utilities/reportDownload.js';

// Safe destructuring of tc function from i18n with fallback for test environment
const _tc =
    i18n.global?.tc ||
    ((key, count) => {
        // Simple fallback for pluralization in test environment
        const pluralMap = {
            second: count === 1 ? 'second' : 'seconds',
            minute: count === 1 ? 'minute' : 'minutes',
            hour: count === 1 ? 'hour' : 'hours',
            day: count === 1 ? 'day' : 'days',
            week: count === 1 ? 'week' : 'weeks',
            month: count === 1 ? 'month' : 'months',
            year: count === 1 ? 'year' : 'years',
        };
        return pluralMap[key] || key;
    });

export function spacify(str) {
    const result = str.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
}

export function oxfordList(items, separator = 'and') {
    if (items.length === 0) {
        return '';
    }

    if (items.length === 1) {
        return items[0];
    }

    if (items.length === 2) {
        return `${items[0]} ${separator} ${items[1]}`;
    }

    return (
        items.slice(0, -2).join(', ') +
        (items.slice(0, -2).length ? ', ' : '') +
        items.slice(-2).join(`, ${separator} `)
    );
}

const delay = (function createDelay() {
    const timers = {};

    return function delayFunction({ callback, ms, uuid }) {
        clearTimeout(timers[uuid]);
        timers[uuid] = setTimeout(callback, ms);
    };
})();

export function getBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });
}

/**
 * Parse PHP ini size values such as "8M", "512K", "1G", or raw bytes.
 */
export function parsePhpIniSize(value) {
    if (value === null || value === undefined || value === '') {
        return 0;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
        return value >= 0 ? Math.floor(value) : 0;
    }

    const trimmed = String(value).trim();
    const match = trimmed.match(/^(\d+(?:\.\d+)?)([KMG])?$/i);

    if (!match) {
        const asNumber = Number(trimmed);
        return Number.isFinite(asNumber) && asNumber >= 0 ? Math.floor(asNumber) : 0;
    }

    const amount = Number(match[1]);
    const unit = (match[2] || '').toUpperCase();

    if (unit === 'G') {
        return Math.floor(amount * 1024 * 1024 * 1024);
    }

    if (unit === 'M') {
        return Math.floor(amount * 1024 * 1024);
    }

    if (unit === 'K') {
        return Math.floor(amount * 1024);
    }

    return Math.floor(amount);
}

/**
 * Max raw file size that can ride a base64-in-JSON autosave PUT without
 * exceeding PHP's post_max_size (after ~33% base64 inflation + JSON envelope).
 */
export function getBase64AutosaveMaxFileBytes() {
    const postMaxBytes = parsePhpIniSize(initialState('PostMaxSize'));
    const appMaxBytes = Number(initialState('MaxFileSize')) || Infinity;

    // Base64 expands ~4/3; leave ~15% of post_max_size for the rest of the activity JSON.
    const postMaxSafeRaw = postMaxBytes > 0 ? Math.floor((postMaxBytes * 0.85 * 3) / 4) : Infinity;

    return Math.min(appMaxBytes, postMaxSafeRaw);
}

export function getBase64AutosaveMaxFileLabel() {
    const display = initialState('MaxFileSizeDisplay');
    const maxBytes = getBase64AutosaveMaxFileBytes();
    const appMaxBytes = Number(initialState('MaxFileSize')) || Infinity;

    // Prefer the configured display label when it matches the effective limit.
    if (display && maxBytes === appMaxBytes) {
        return display;
    }

    if (!Number.isFinite(maxBytes)) {
        return display || 'the allowed limit';
    }

    const mb = maxBytes / (1024 * 1024);
    if (mb >= 1) {
        return `${Math.floor(mb * 10) / 10} MB`;
    }

    return `${Math.max(1, Math.floor(maxBytes / 1024))} KB`;
}

/**
 *
 * @param {Date} date
 * @param {string} [timezone]
 * @returns {*}
 */
export function localDateToUTC(date, timezone) {
    const convertDate = date instanceof Date ? date : new Date(date);

    const converted = moment(convertDate);

    // A falsy timezone turns moment's .tz() into a getter that returns the default-zone
    // *string* (vue-moment sets a default), so .utc() would throw "tz(...).utc is not a
    // function". The same instant resolves to UTC with or without the display-zone hop.
    return timezone ? converted.tz(timezone).utc() : converted.utc();
}

/**
 * Get the start of day (midnight) for a given date in the specified timezone.
 * Uses the JavaScript Date's local date components to avoid timezone conversion issues.
 *
 * @param {Date} date
 * @param {string} timezone
 * @param {string} [format='YYYY-MM-DD HH:mm:ss']
 */
export function startOfDayInTimezone(date, timezone, format = 'YYYY-MM-DD HH:mm:ss') {
    if (!(date instanceof Date)) {
        throw new Error('Parameter must be a native Date object.');
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return moment.tz(`${year}-${month}-${day} 00:00:00`, format, timezone);
}

/**
 * Get the end of day (23:59:59) for a given date in the specified timezone.
 * Uses the JavaScript Date's local date components to avoid timezone conversion issues.
 *
 * @param {Date} date
 * @param {string} timezone
 * @param {string} [format='YYYY-MM-DD HH:mm:ss']
 */
export function endOfDayInTimezone(date, timezone, format = 'YYYY-MM-DD HH:mm:ss') {
    if (!(date instanceof Date)) {
        throw new Error('Parameter must be a native Date object.');
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return moment.tz(`${year}-${month}-${day} 23:59:59`, format, timezone);
}

export function UTCToLocalDate(date, timezone) {
    if (!(date instanceof Date)) {
        throw new Error('Parameter must be a native Date object.');
    }

    return new Date(moment.utc(date).toDate(), { timeZone: timezone });
}

export function daysBetweenDates(start, end, timezone) {
    const dates = [];

    const currentDate = timezone ? moment.utc(start).tz(timezone).startOf('day') : moment(start).startOf('day');

    const lastDate = timezone ? moment.utc(end).tz(timezone).endOf('day') : moment(start).endOf('day');

    do {
        dates.push(currentDate.clone());
    } while (currentDate.add(1, 'day').diff(lastDate) < 0);

    return dates;
}

/**
 * Format a date-only value (e.g. a Laravel `date` cast) for display.
 *
 * These values serialize as `Y-m-d H:i:s` at midnight UTC with no offset. Running them
 * through `.tz(displayZone)` (as timestamp columns do) rolls the calendar date back a day
 * for viewers in behind-UTC timezones, so a due date stored as 2026-04-01 renders as
 * "March 31, 2026". Parsing and formatting in UTC keeps the stored calendar date intact.
 *
 * @param {string|Date} date
 * @param {string} [format='LL']
 * @returns {string}
 */
export function formatDateOnly(date, format = 'LL') {
    return moment.utc(date).format(format);
}

function parseDownloadFileName(contentDisposition, name = null) {
    if (!contentDisposition) {
        throw new Error('The response did not generate a file for download.');
    }

    const fileNameFromHeader = get(contentDisposition.match(/^attachment; filename="(.+)".*$/), 1, 'file.txt');
    const rawFileName = name || fileNameFromHeader;

    try {
        return decodeURIComponent(rawFileName);
    } catch {
        return rawFileName;
    }
}

function saveBlobToDisk(blob, fileName) {
    const pseudoUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = pseudoUrl;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(pseudoUrl);
}

async function assertBlobDownloadResponse(response) {
    const contentType = response.headers?.['content-type'] || '';

    // Some error payloads can arrive as 2xx JSON blobs without a Content-Disposition header.
    if (!contentType.includes('application/json')) {
        return;
    }

    const text = await readBlobAsText(response.data);
    let payload = {};

    try {
        payload = JSON.parse(text);
    } catch {
        payload = { message: text || 'Unable to download the file.' };
    }

    const error = new Error(payload.message || 'Unable to download the file.');
    error.response = { status: response.status, data: payload, headers: response.headers };
    throw error;
}

function openBlobDownload(blob, open) {
    if (!open) {
        return;
    }

    const openUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
    window.open(openUrl);
}

async function hydrateDownloadBlobError(error) {
    const data = error?.response?.data;

    if (!(data instanceof Blob)) {
        return;
    }

    const text = await readBlobAsText(data);

    try {
        error.response.data = JSON.parse(text);
    } catch {
        error.response.data = { message: text || 'Unable to download the file.' };
    }
}

/**
 * Download a file from the server
 * @param {string} url
 * @param {string|null} [name]
 * @param {object} [config]
 * @param {boolean} [open]
 * @returns {Promise<void>}
 */
export async function downloadFile({ url, name = null, config = {}, open = false }) {
    try {
        const response = await axios.get(url, { responseType: 'blob', ...config });
        await assertBlobDownloadResponse(response);
        openBlobDownload(response.data, open);
        saveBlobToDisk(response.data, parseDownloadFileName(response.headers['content-disposition'], name));
    } catch (error) {
        await hydrateDownloadBlobError(error);
        throw error;
    }
}

/**
 * Let the browser download a file directly from the server so the response can stream
 * to disk instead of being buffered in JavaScript first.
 *
 * @param {string} url
 * @returns {Promise<void>}
 */
export function triggerBrowserDownload(url) {
    const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;

    return new Promise((resolve, reject) => {
        const frame = document.createElement('iframe');

        frame.style.cssText = 'display:none;width:0;height:0;border:0';
        frame.setAttribute('aria-hidden', 'true');

        let settled = false;

        const settle = (error = null) => {
            if (settled) {
                return;
            }

            settled = true;
            window.clearTimeout(timeoutId);
            window.setTimeout(() => frame.remove(), 1000);

            if (error) {
                reject(error);
            } else {
                resolve();
            }
        };

        frame.addEventListener('load', () => {
            try {
                const text = frame.contentDocument?.body?.innerText?.trim();

                if (text?.startsWith('{')) {
                    const data = JSON.parse(text);
                    const error = new Error(`Request failed with status code ${data.status || 403}`);

                    error.response = {
                        status: data.status || 403,
                        data,
                    };

                    settle(error);

                    return;
                }
            } catch {
                // Download started or the frame is empty after the browser took over.
            }

            settle();
        });

        frame.addEventListener('error', () => settle(new Error('Download failed')));

        const timeoutId = window.setTimeout(() => settle(), 10 * 60 * 1000);

        document.body.appendChild(frame);

        const link = document.createElement('a');
        link.href = fullUrl;
        link.target = frame.name = `browser-download-${Date.now()}`;
        link.rel = 'noopener';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        link.remove();
    });
}

export function initialState(key, defaultValue) {
    // eslint-disable-next-line no-underscore-dangle
    return get(window.__INITIAL_STATE__, key, defaultValue);
}

/**
 * Build display moments for a week/break's stored UTC start and end using the institution
 * timezone from the initial app state — the same source the course editor header uses
 * (initialState('InstitutionSettings.timezone')). Falls back to the raw UTC value when no
 * timezone is available.
 *
 * There is intentionally no DST hour-nudging here: week boundaries are derived calendar dates,
 * so a single conversion in the institution timezone is sufficient and keeps the course side
 * menu in agreement with the editor header (which uses this same source and no nudge).
 *
 * @param {string} weekStart UTC datetime string
 * @param {string} weekEnd UTC datetime string
 * @returns {{ start: import('moment').Moment, end: import('moment').Moment }}
 */
export function weekRangeInInstitutionTz(weekStart, weekEnd) {
    const timezone = initialState('InstitutionSettings.timezone');
    const toMoment = value => (timezone ? moment.utc(value).tz(timezone) : moment.utc(value));

    return {
        start: toMoment(weekStart),
        end: toMoment(weekEnd),
    };
}

/**
 * Parse a human-readable size label (e.g. "20 MB") into bytes for upload validation.
 */
export function parseDisplayFileSize(display) {
    if (!display || typeof display !== 'string') {
        return null;
    }

    const match = display.trim().match(/^([\d.]+)\s*(B|KB|MB|GB|TB)?$/i);

    if (!match) {
        return null;
    }

    const value = Number.parseFloat(match[1]);
    const unit = (match[2] || 'B').toUpperCase();
    const multipliers = {
        B: 1,
        KB: 1024,
        MB: 1024 ** 2,
        GB: 1024 ** 3,
        TB: 1024 ** 4,
    };

    return Math.round(value * (multipliers[unit] ?? 1));
}

export function getMaxUploadFileSizeBytes(displayLabel = null) {
    const label = displayLabel ?? initialState('MaxFileSizeDisplay', '20 MB');
    const parsed = parseDisplayFileSize(label);

    if (parsed !== null) {
        return parsed;
    }

    return Number.parseInt(initialState('MaxFileSize'), 10) || 1024 * 1024 * 20;
}

export function handleResponseError(error) {
    // While the impersonation-expiry redirect is in flight the page is about to
    // unload; concurrent requests fail transiently (409/403/etc.), so suppress
    // their snackbars. The payload check covers the 409 that starts the redirect.
    if (
        ImpersonationExpiryMonitor.isRedirectInProgress() ||
        (error?.response?.status === 409 && error?.response?.data?.impersonation_expired)
    ) {
        return;
    }

    // 401s are session expiry (e.g. logout in another tab / background polls).
    // SessionMonitor owns one redirect + login notice — never stack unauthorized toasts.
    if (error?.response?.status === 401) {
        if (sessionMonitor.shouldSuppressError(error)) {
            return;
        }

        if (!sessionMonitor.isPublicPath(window.location.pathname)) {
            sessionMonitor.handleSessionTimeout();
            store.dispatch('Auth/setSessionTimedOut', true);
            store.dispatch('Auth/clear');
        }

        return;
    }

    const errorPackage = new ErrorPackage(initialState('Debug'));

    errorPackage.capture(error);

    // For 422 validation errors, don't show a generic snackbar.
    // The AutoSave system will display specific field-level validation errors
    // which are more helpful to users than "Please correct the fields with invalid data."
    if (error?.response?.status === 422) {
        errorPackage.disableSnackbar();
        errorPackage.userMessage = null;
        store.commit('PUSH_GLOBAL_ERROR', errorPackage);
        return;
    }

    // Generate a user-friendly message for display in snackbars
    const userMessage = errorPackage.getUserFriendlyMessage();

    // If getUserFriendlyMessage returns null (e.g., for access denied errors),
    // don't show a snackbar but still capture the error for debugging
    if (userMessage === null) {
        errorPackage.disableSnackbar();
        errorPackage.userMessage = null;
    } else {
        errorPackage.userMessage = userMessage;
    }

    store.commit('PUSH_GLOBAL_ERROR', errorPackage);
}

/**
 * Get a setting's value from any of the setting groups in the following priority: User, Institution, Environment.
 * @param key
 * @param defaultValue
 * @returns {unknown}
 */
export function setting(key, defaultValue) {
    let value = initialState(`UserSettings.${key}`, null);

    if (!value) value = initialState(`InstitutionSettings.${key}`, null);
    if (!value) value = initialState(`EnvironmentSettings.${key}`, null);

    return value ?? defaultValue;
}

export const MONTH = 30 * 24 * 60 * 60;
export const WEEK = 7 * 24 * 60 * 60;
export const DAY = 24 * 60 * 60;
export const HOUR = 60 * 60;
export const MINUTE = 60;

function getTimeUnit(seconds) {
    const timeUnits = [
        { divisor: MONTH, unit: 'month' },
        { divisor: WEEK, unit: 'week' },
        { divisor: DAY, unit: 'day' },
        { divisor: HOUR, unit: 'hour' },
        { divisor: MINUTE, unit: 'minute' },
    ];

    if (seconds === 0) {
        return { divisor: 1, unit: 'second' };
    }

    const matchingUnit = timeUnits.find(({ divisor }) => seconds % divisor === 0);
    return matchingUnit || { divisor: 1, unit: 'second' };
}

export function displaySeconds(seconds) {
    if (!seconds) {
        return 'N/A';
    }

    const { divisor, unit } = getTimeUnit(seconds);
    const units = seconds / divisor;
    // Pass the numeric count to trigger pluralization (e.g., "minute|minutes")
    return `${units} ${i18n.global.t(unit, units)}`;
}

function formatDurationPart(duration, unit, abbrev) {
    let value;

    if (unit === 'year') {
        value = Math.floor(duration.years());
    } else if (unit === 'month') {
        value = Math.floor(duration.months());
    } else if (unit === 'day') {
        value = Math.floor(duration.days());
    } else if (unit === 'hour') {
        value = Math.floor(duration.hours());
    } else if (unit === 'minute') {
        value = Math.floor(duration.minutes());
    } else if (unit === 'second') {
        value = Math.floor(duration.seconds());
    } else {
        return null;
    }

    if (value < 1) return null;

    if (abbrev) {
        const abbrevUnit = unit.charAt(0);
        return `${value}${abbrevUnit}`;
    }

    const pluralUnit = value > 1 ? `${unit}s` : unit;
    return `${value} ${pluralUnit}`;
}

/**
 * @todo Support translations
 * @param period
 * @returns {string}
 */
export function formatDuration(period, abbrev = false) {
    const duration = moment.duration(period);

    // return nothing when the duration is falsy or not correctly parsed (P0D)
    if (!duration || duration.toISOString() === 'P0D') return '';

    const parts = [];
    const units = ['year', 'month', 'day', 'hour', 'minute', 'second'];

    units.forEach(unit => {
        const part = formatDurationPart(duration, unit, abbrev);
        if (part) parts.push(part);
    });

    return parts.join(', ');
}

export { delay };

export function field(name) {
    return {
        get() {
            return this.getField(name);
        },

        set(value) {
            this.setField(name, value);
        },
    };
}

export function relativeTimestamp(
    timestamp,
    { pastLabel = 'Occurred on', futureLabel = 'Scheduled for', longFormat = true } = {}
) {
    const isFuture = moment().isBefore(timestamp);
    const relevance = isFuture ? futureLabel || 'Occurred on' : pastLabel || 'Scheduled for';

    const fullFormat = longFormat ? 'LLL zz' : 'ddd M/D [at] h:mm A';

    return timestamp?.calendar(null, {
        sameDay: '[Today at] h:mm A',
        nextDay: '[Tomorrow at] h:mm A',
        nextWeek: 'dddd [at] h:mm A',
        lastDay: '[Yesterday at] h:mm A',
        lastWeek: 'dddd [at] h:mm A',
        sameElse: `[${relevance}] ${fullFormat}`,
    });
}

export function ellipsify(text, maxWords = 100) {
    const words = text.trim().split(/\s+/);

    if (words.length <= maxWords) {
        return text;
    }
    return `${words.slice(0, maxWords).join(' ')}...`;
}

export async function asyncForEach(array, callback) {
    return Promise.all(array.map(callback));
}

export function modelToString(model) {
    return spacify(model.replace('App\\Models\\', ''));
}

/**
 * Removes HTML tags from a given string.
 *
 * @param {string} str - The string to sanitize.
 * @returns {string} - The string without HTML tags.
 */
export function removeHTMLTags(str) {
    return typeof str === 'string' ? str.replace(/<\/?[^>]+(>|$)/g, '') : '';
}

export function rootDomain(hostname) {
    const parts = hostname.split('.');

    if (parts.length > 2) {
        const tlds = ['co.uk', 'gov.uk', 'ac.uk', 'com.au', 'org.au'];
        const lastTwoParts = parts.slice(-2).join('.');

        if (tlds.includes(lastTwoParts)) {
            return parts.slice(-3).join('.');
        }
    }

    return parts.slice(-2).join('.');
}

/**
 * Get signed cookies from the server in order to retrieve CDN content.
 *
 * @param service
 * @returns {Promise<void|any>}
 */
export async function getSignedCookies(service) {
    const requiredCookies = ['CloudFront-Key-Pair-Id', 'CloudFront-Policy', 'CloudFront-Signature'];

    if (requiredCookies.every(cookie => Cookies.get(cookie))) {
        return null;
    }

    const { data } = await axios.get(`/api/signed-cookies?service=${service}`);

    Object.entries(data.data).forEach(([key, value]) => {
        Cookies.set(key, value, {
            domain: rootDomain(initialState('AppHost')),
        });
    });

    return data;
}

/**
 * Generate a two‐letter avatar monogram from a given firstName and lastName.
 * Handles multi‐word names, skips common “particles,” and falls back to single‐token logic.
 *
 * @param {string} firstName  – The user’s given name(s), e.g. "María" or "Juan Martín"
 * @param {string} lastName   – The user’s family name(s), e.g. "García López" or "van Beethoven"
 * @returns {string} Two uppercase initials, or an empty string if no valid letters are found.
 */
/* eslint-disable complexity */
export function initials(firstName, lastName) {
    // If both inputs are missing or empty, return empty string.
    if (!firstName && !lastName) {
        return '';
    }

    // 1. Combine firstName + lastName into one raw string, trim whitespace.
    const raw = `${firstName || ''} ${lastName || ''}`.trim();
    if (raw === '') {
        return '';
    }

    // 2. Split into tokens on whitespace, hyphens, or apostrophes.
    //    e.g. "Anne‐Marie O'Connor" → ["Anne", "Marie", "O", "Connor"]
    let tokens = raw.split(/\s+|[-–—]+|['’]+/);

    // 3. Strip out any non‐letter characters (using Unicode letter property).
    //    If a token becomes empty after stripping, drop it.
    tokens = tokens.map(token => token.replace(/[^\p{L}]/gu, '')).filter(token => token.length > 0);

    if (tokens.length === 0) {
        return '';
    }

    // 4. If there's exactly one token, use its first two letters (or duplicate if length is 1).
    if (tokens.length === 1) {
        const single = tokens[0];
        if (single.length >= 2) {
            return (single[0] + single[1]).toUpperCase();
        }
        // e.g. name "Q" → "QQ"
        return (single[0] + single[0]).toUpperCase();
    }

    // 5. If there are 2 or more tokens, pick:
    //    - firstInitial: first character of tokens[0]
    //    - lastInitial: first character of tokens[lastNonParticleIndex]
    //
    //    We skip over any “particles” (e.g. "de", "da", "del", "van", "von", etc.)
    //    when choosing the “last” token.

    // 5a. Define a small list of common particles/connectors to skip.
    const particles = ['da', 'de', 'del', 'di', 'do', 'dos', 'du', 'la', 'le', 'van', 'von', 'bin', 'al', 'al-', 'abu'];

    // 5b. Find the last token index that is not in the particles list.
    let lastIdx = tokens.length - 1;
    while (lastIdx > 0 && particles.includes(tokens[lastIdx].toLowerCase())) {
        lastIdx--;
    }

    // 6. Grab first letter of the first token, and first letter of the chosen last token.
    const firstInitial = tokens[0][0] || '';
    const lastInitial = tokens[lastIdx][0] || '';

    // If for some reason either initial is missing, fall back to single‐token logic:
    if (!firstInitial && !lastInitial) {
        return '';
    }
    if (firstInitial && !lastInitial) {
        const fallback = tokens[0];
        return fallback.length >= 2
            ? (fallback[0] + fallback[1]).toUpperCase()
            : (fallback[0] + fallback[0]).toUpperCase();
    }
    if (!firstInitial && lastInitial) {
        const fallback = tokens[lastIdx];
        return fallback.length >= 2
            ? (fallback[0] + fallback[1]).toUpperCase()
            : (fallback[0] + fallback[0]).toUpperCase();
    }

    // 7. Return the two initials in uppercase.
    return (firstInitial + lastInitial).toUpperCase();
}
/* eslint-enable complexity */

export function errorMessageForStatus(status) {
    switch (status) {
        case 400:
            return 'An error occurred while processing your request.';
        case 401:
            return 'Please log in to continue.';
        case 403:
            return 'You are not authorized to access this resource.';
        case 404:
            return 'The resource you are looking for does not exist.';
        case 405:
            return 'The method you are trying to use is not allowed.';
        case 409:
            return 'The resource you are trying to create already exists.';
        case 422:
            return 'A validation error occurred while processing your request.';
        default:
            return 'An error occurred while processing your request.';
    }
}

/**
 * File Type Validation Utilities
 */

/**
 * Validates if a file matches any of the allowed MIME types or extensions
 * @param {File} file - The file to validate
 * @param {Array} allowedMimeTypes - Array of allowed MIME types
 * @param {Array} allowedExtensions - Array of allowed file extensions (with dots, e.g. ['.pdf', '.png'])
 * @returns {boolean} - True if file is valid, false otherwise
 */
export function validateFileType(file, allowedMimeTypes = [], allowedExtensions = []) {
    if (!file) return false;

    // Check MIME type first (most reliable)
    if (allowedMimeTypes.length > 0 && allowedMimeTypes.includes(file.type)) {
        return true;
    }

    // Fallback to extension check for files with generic or missing MIME types
    if (allowedExtensions.length > 0) {
        const fileName = file.name.toLowerCase();
        return allowedExtensions.some(ext => fileName.endsWith(ext.toLowerCase()));
    }

    // If no allowed types specified, consider it valid
    return allowedMimeTypes.length === 0 && allowedExtensions.length === 0;
}

/**
 * Gets a user-friendly file extension from a file
 * @param {File} file - The file to get extension from
 * @returns {string} - The file extension with dot (e.g. '.pdf')
 */
export function getFileExtension(file) {
    if (!file || !file.name) return '';
    const parts = file.name.split('.');
    return parts.length > 1 ? `.${parts.pop().toLowerCase()}` : '';
}

/**
 * Generates a user-friendly error message for invalid file types
 * @param {File} file - The invalid file
 * @param {Array} allowedExtensions - Array of allowed file extensions
 * @param {string} customMessage - Optional custom message
 * @returns {string} - Error message
 */
export function generateFileTypeErrorMessage(file, allowedExtensions = [], customMessage = null) {
    const fileExtension = getFileExtension(file);
    const baseMessage = `The file "${file.name}" (${fileExtension}) is not a supported file type.`;

    if (customMessage) {
        return `${baseMessage} ${customMessage}`;
    }

    if (allowedExtensions.length > 0) {
        return `${baseMessage} Please upload a file with one of these extensions: ${allowedExtensions.join(', ')}.`;
    }

    return baseMessage;
}

/**
 * Predefined file type configurations for common upload scenarios
 */
export const FILE_TYPE_CONFIGS = {
    slideDeck: {
        mimeTypes: [
            'application/vnd.apple.keynote',
            'application/vnd.oasis.opendocument.presentation',
            'application/mspowerpoint',
            'application/vnd.openxmlformats-officedocument.presentationml.template',
            'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
            'application/vnd.ms-powerpoint',
            'application/vnd.ms-powerpoint.presentation.macroenabled.12',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            'application/vnd.stardivision.draw',
        ],
        extensions: ['.pptx', '.ppt', '.odp', '.potx', '.ppsx', '.pptm', '.pot', '.pps', '.key', '.sda'],
        message: 'Please upload a slide deck file.',
    },
    video: {
        mimeTypes: ['video/mp4', 'video/quicktime'],
        extensions: ['.mp4', '.mov'],
        message: 'Please upload a video file (MP4 or MOV).',
    },
    image: {
        mimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        extensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
        message: 'Please upload an image file.',
    },
    document: {
        mimeTypes: [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'text/plain',
        ],
        extensions: ['.pdf', '.doc', '.docx', '.txt'],
        message: 'Please upload a document file.',
    },
};

/**
 * Validates multiple files against a file type configuration
 * @param {FileList|Array} files - Files to validate
 * @param {Object} config - File type configuration (from FILE_TYPE_CONFIGS or custom)
 * @returns {Object} - Validation result with { valid: File[], invalid: File[], errors: string[] }
 */
export function validateFiles(files, config) {
    const fileArray = Array.from(files);
    const valid = [];
    const invalid = [];
    const errors = [];

    fileArray.forEach(file => {
        if (validateFileType(file, config.mimeTypes, config.extensions)) {
            valid.push(file);
        } else {
            invalid.push(file);
            errors.push(generateFileTypeErrorMessage(file, config.extensions, config.message));
        }
    });

    return { valid, invalid, errors };
}

/**
 * The suffix every page title ends with, e.g. "Edverum (Acme)" or "Edverum".
 *
 * Built server-side (InstitutionSettingsService::titleSuffix) and injected into
 * window.config.institution.titleSuffix. Centralized here so the router and the
 * breadcrumb resolve it identically and cannot drift.
 *
 * @returns {string}
 */
export function appTitleSuffix() {
    return window.config?.institution?.titleSuffix || window.config?.appName || 'Edverum';
}
